import React from "react";
import { useTranslation } from "react-i18next";
import { formatCardNumber, formatExpiry, type StoneCardFields } from "../lib/stoneCard.js";

/**
 * Card fields for Stone 12x (transparent checkout). The values never leave the
 * browser except to Pagar.me's tokenization endpoint (see lib/stoneCard.ts).
 */
export function StoneCardForm({
  value,
  onChange,
  installmentsLabel,
  disabled,
}: {
  value: StoneCardFields;
  onChange: (next: StoneCardFields) => void;
  installmentsLabel?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<StoneCardFields>) => onChange({ ...value, ...patch });
  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="field-label flex items-center justify-between w-full">
        <span>{t("checkout.stone.cardTitle")}</span>
        {installmentsLabel && <span className="text-xs font-medium text-purple-deep">{installmentsLabel}</span>}
      </legend>
      <input
        className="input-clean"
        type="text"
        inputMode="numeric"
        autoComplete="cc-number"
        name="cc-number"
        placeholder={t("checkout.stone.numberPlaceholder")}
        aria-label={t("checkout.stone.number")}
        value={value.number}
        onChange={(e) => set({ number: formatCardNumber(e.target.value) })}
        required
      />
      <input
        className="input-clean"
        type="text"
        autoComplete="cc-name"
        name="cc-name"
        placeholder={t("checkout.stone.holderPlaceholder")}
        aria-label={t("checkout.stone.holder")}
        value={value.holderName}
        onChange={(e) => set({ holderName: e.target.value })}
        required
      />
      <div className="grid grid-cols-2 gap-3">
        <input
          className="input-clean"
          type="text"
          inputMode="numeric"
          autoComplete="cc-exp"
          name="cc-exp"
          placeholder={t("checkout.stone.expiryPlaceholder")}
          aria-label={t("checkout.stone.expiry")}
          value={value.expiry}
          onChange={(e) => set({ expiry: formatExpiry(e.target.value) })}
          required
        />
        <input
          className="input-clean"
          type="text"
          inputMode="numeric"
          autoComplete="cc-csc"
          name="cc-csc"
          placeholder={t("checkout.stone.cvvPlaceholder")}
          aria-label={t("checkout.stone.cvv")}
          maxLength={4}
          value={value.cvv}
          onChange={(e) => set({ cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
          required
        />
      </div>
    </fieldset>
  );
}
